import { AuthenticationResult, InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";
import { IMsalContext } from "@azure/msal-react";

import { apiRequest } from "../config/authConfig";
import { AppConfig } from "../config/appConfig";

import { SharedStoreService } from "../services/sharedStoreService";

import { ApplicationHub, ApplicationHubMessageSubject, IApplicationHubMessage, IMessageSubscription, UpdateClaimType } from "../types/appHub";

/**
 * A singleton that manages the access and lifecycle of MSAL authorization tokens.
 */
export class CredentialManager {
	private static _instance: CredentialManager = new CredentialManager();
	private _currentCredentials?: AuthenticationResult;
	private _applicationHub?: ApplicationHub;
	private _timer?: NodeJS.Timeout;
	private _msalContext?: IMsalContext;
	private _debugLogging: boolean = false;
	private _isLoadingSavedCredentials: boolean = true;
	private _cancelPolling: boolean = false;
	private _tokenRefreshStartDate?: Date;
	private _hasInitialized: boolean = false;

	get hasValidCredentials(): boolean {
		return !!this._currentCredentials?.accessToken && !this.isAccessTokenExpired();
	}

	get ready(): boolean {
		return !this._isLoadingSavedCredentials;
	}

	/** Event to signal that the CredentialManager has finished initialization. */
	onInitialized: InitializedHandler | undefined;

	/**
	 * The amount of time to wait between token expiration checks in milliseconds.
	 */
	expirationPollMs: number = AppConfig.credentialManagerExpirationPollMs;

	/**
	 * The maximum number of times to try getting a new access token before giving up.
	 */
	maxRetryAttempts: number = AppConfig.credentialManagerMaxRetryAttempts;

	/**
	 * The amount of time before access token expiration to get a new token in milliseconds.
	 */
	refreshAfterRemainingMs: number = AppConfig.credentialManagerRefreshAfterRemainingMs;

	/**
	 * The amount of time between failed attempts to get a new access token in milliseconds.
	 */
	retryAttemptDelayMs: number = AppConfig.credentialManagerRetryAttemptDelayMs;

	/**
	 * Get the CredentialManager singleton's instance
	 * @returns A CredentialManager instance
	 */
	static getInstance(): CredentialManager {
		return this._instance;
	}

	private constructor() {
		this._applicationHub = ApplicationHub.getInstance();
		this._applicationHub.subscribeToMessages({ name: "credentialManager", handler: this.handleApplicationHubMessage } as IMessageSubscription);
	}

	/**
	 * Send the provided message and data to the console if debug logging is enabled.
	 * @param message The message to log.
	 * @param data Optional accompanying data.
	 */
	private logToConsole(message: string, ...data: any[]): void {
		if (this._debugLogging) {
			console.log(`[${new Date().toISOString()}] CredentialManager: ${message}`, ...data);
		}
	}

	/**
	 * Initialize CredentialManager by loading stored credentials and refreshing if necessary.
	 */
	async initialize(): Promise<void> {
		if (this._hasInitialized) return;
		this._hasInitialized = true;
		this.logToConsole("Initializing");
		await this.applyStoredCredentials();
		if (!this._currentCredentials?.accessToken || this.isAccessTokenExpired()) {
			if (this._msalContext?.accounts[0] == undefined) {
				this.logToConsole("No active account found on initialization. Cannot refresh credentials.");
			}
			else {
				this.logToConsole("Stored credentials are expired on initialization. Forcing immediate refresh.");
				await this.forceRefresh();
			}
		}
		this.logToConsole("Initialization complete.");
		this.onInitialized?.();
	}

	/**
	 * Load the last credential snapshot from the shared store.
	 */
	async applyStoredCredentials(): Promise<void> {
		try {
			const credentials = await SharedStoreService.Credentials.get();
			if (credentials) {
				this.logToConsole("Loaded stored credentials");
				this._currentCredentials = credentials;
				this.startExpiryPolling();
			}
			else {
				this.logToConsole("No stored credentials found");
			}			
		} catch (error) {
			console.error("ERROR: CredentialManager: Error loading stored credentials", error);
		} finally {
			this._isLoadingSavedCredentials = false;
		}
	}

	/**
	 * Handle incoming Application Hub messages.
	 * @param message The incoming Application Hub message.
	 */
	handleApplicationHubMessage = async (message: IApplicationHubMessage): Promise<void> => {
		if (message?.subject === ApplicationHubMessageSubject.NewAccessToken) {
			this.logToConsole(`Received NewAccessToken message from instance ${message.senderInstanceId} over the Application Hub. Refreshing stored credentials.`);
			this.stopExpiryPolling();
			await this.applyStoredCredentials();
			this.startExpiryPolling();
		}
	}

	/**
	 * Clear stored credentials
	 */
	async clearCredentials(): Promise<void> {
		this.logToConsole("Clearing credentials");
		this.stopExpiryPolling();
		this._currentCredentials = undefined;
		await SharedStoreService.Credentials.clear();
	}

	/**
	 * Schedule an access token update using the Application Hub to prevent multiple instances from updating at the same time.
	 */
	async scheduleUpdate() {
		const claim = await SharedStoreService.UpdateClaims.get(UpdateClaimType.AccessToken);
		if (!claim) {
			// No claim exists, create one and wait for the next expiry check to perform the update if we maintain the claim.
			this.logToConsole(`No access token update claim exists. Creating. (Instance ID: [${this._applicationHub?.instanceId}])`);
			await SharedStoreService.UpdateClaims.create(UpdateClaimType.AccessToken);
		}
		else if (claim.instanceId !== this._applicationHub?.instanceId) {
			// Another instance has claimed the update. Yeild to that instance so long as it hasn't abandoned its claim.
			if (claim.hasExpired) {
				this.logToConsole(`Access token update claim from another instance has expired. Taking over the claim. (Claim instance: [${claim.instanceId}], This instance: [${this._applicationHub?.instanceId}])`);
				await SharedStoreService.UpdateClaims.remove(claim.type);
				await SharedStoreService.UpdateClaims.create(UpdateClaimType.AccessToken);
				return;
			}
			this.logToConsole(`Access token update claim already exists from another instance. Yeilding. (Claim instance: [${claim.instanceId}], This instance: [${this._applicationHub?.instanceId}])`);
		}
		else {
			// The waiting claim is for our instance. Proceed with the update.
			this.logToConsole(`Access token update claim exists for this instance. Proceeding with update. (Instance ID: [${this._applicationHub?.instanceId}])`);
			try {
				this._cancelPolling = true;
				await SharedStoreService.UpdateClaims.update(claim);
				await this.forceRefresh();
				this.logToConsole("New access token has been acquired and set. Removing update claim.");
				await SharedStoreService.UpdateClaims.remove(UpdateClaimType.AccessToken);
			} catch (error) {
				console.error("ERROR: CredentialManager.scheduleUpdate: Error acquiring new access token.", error);
			} finally {
				this.startExpiryPolling();
			}
		}
	}

	/**
	 * Retrieve new credentials immediately regardless of expiration date
	 */
	async forceRefresh(): Promise<void> {
		this._tokenRefreshStartDate = new Date();
		this.logToConsole("Force refreshing credentials");
		await this.clearCredentials();
		await this.setCredentials(await this.retrieveNewToken());
		this._tokenRefreshStartDate = undefined;
	}

	/**
	 * Get the current access token.
	 * @returns The current access token as a string.
	 */
	async getAccessToken(): Promise<string> {
		if (!this._currentCredentials) {
			this.logToConsole("getAccessToken: Current credentials are not set. Scheduling update.");
			await this.scheduleUpdate();
		}
		if (!this._currentCredentials?.accessToken) {
			if (this._tokenRefreshStartDate) {
				this.logToConsole(`getAccessToken: Attempt to get access token while it is being refreshed. (Instance ID: [${this._applicationHub?.instanceId}])`);
				throw new Error("AccessToken is being refreshed. Please try again later.");
			}
			else {
				this.logToConsole(`getAccessToken: No access token available. (Instance ID: [${this._applicationHub?.instanceId}])`);
				throw new Error("AccessToken is not available.");
			}
		}
		return this._currentCredentials.accessToken;
	}

	/**
	 * Get a new set of MSAL credentials.
	 * @returns MSAL credentials as an AuthenticationResult promise.
	 */
	private async retrieveNewToken(): Promise<AuthenticationResult> {
		this.logToConsole("Retrieving new MSAL token");
		let lastError;
		const instance = this.getMsalInstance();
		for (let i = 0; i < this.maxRetryAttempts; i++) {
			try {
				this.logToConsole("Requesting new token...");
				const account = this._msalContext?.accounts[0];
				return await instance.acquireTokenSilent({ ...apiRequest, account });
			}
			catch (error) {
				if ((error as InteractionRequiredAuthError)?.message?.search('AADSTS50058')) {
					this.logToConsole("Silent auth with no logins error");

					// AADSTS50058 means our MSAL credentails have expired. Direct the user to log in again
					this.clearCredentials();
					instance.logoutRedirect({ account: instance.getActiveAccount() });
					return;
				}
				else {
					this.logToConsole("Some other error: " + error);
					lastError = error;
					await new Promise(resolve => setTimeout(resolve, this.retryAttemptDelayMs));
				}
			}
		}
		throw new Error(`Unable to refresh access token after ${this.maxRetryAttempts} attempts.`, { cause: lastError });
	}

	/**
	 * Get an instance of the MSAL client
	 * @returns An MSAL instance
	 */
	getMsalInstance(): IPublicClientApplication {
		if (!this._msalContext?.instance) {
			throw new Error("No MSAL instance found attempting to communicate with authentication services. Please provide one with CredentialManager.setMsalInstance and try again.");
		}
		return this._msalContext?.instance;
	}

	/**
	 * Check if the current time is greater than the access token expiration date minus refreshAfterRemainingMs.
	 * @returns True if the access token has expired.
	 */
	private isAccessTokenExpired(): boolean {
		this.logToConsole("Checking if current token is expired");
		const expireTime = this._currentCredentials?.expiresOn?.getTime() || 0;
		const now = (new Date()).getTime();
		const msUntilExpire = expireTime - now;
		const msUntilRefreshWindow = msUntilExpire - this.refreshAfterRemainingMs;
		if (msUntilExpire <= 0) {
			this.logToConsole("Token has expired.");
		}
		else if (msUntilRefreshWindow <= 0) {
			this.logToConsole(`Token is valid but is within the refresh window of ${Math.floor(this.refreshAfterRemainingMs / 1000)} seconds.`);
		}
		else {
			this.logToConsole(`Current token is valid for ${Math.floor(msUntilExpire / 1000)} second(s).`);
		}
		return msUntilRefreshWindow <= 0;
	}

	/**
	 * Get the MSAL context required to communicate with authentication services.
	 * @returns The MSAL context
	 */
	static getMsalContext(): IMsalContext | undefined {
		return this._instance._msalContext;
	}

	/**
	 * Sets MSAL context required to communicate with authentication services.
	 * @param instance The MSAL context
	 */
	static setMsalContext(context: IMsalContext): void {
		this._instance._msalContext = context;
	}

	/**
	 * Save the provided MSAL credentials and starts monitoring for expiration.
	 * @param credentials The response from an MSAL request for a token.
	 */
	async setCredentials(credentials: AuthenticationResult): Promise<void> {
		this.logToConsole("Storing new credentials and alerting other instances.");
		this._currentCredentials = credentials;
		await SharedStoreService.Credentials.set(credentials);
		this._applicationHub?.broadcast(ApplicationHubMessageSubject.NewAccessToken)
		this.startExpiryPolling();
	}

	/**
	 * Start polling the access token's expiration date and refreshes it when necessary.
	 */
	private async startExpiryPolling(): Promise<void> {
		if (this._cancelPolling) {
			this.logToConsole("Polling cancel flag has been raised. Aborting start timer.");
			this.stopExpiryPolling();
			this._cancelPolling = false;
			return;
		}

		if (this.isAccessTokenExpired()) {
			this.logToConsole("Refreshing access token.");
			this.scheduleUpdate();
		}
		this._timer = setTimeout(() => { this.startExpiryPolling() }, this.expirationPollMs);
	}

	/**
	 * Clear the timer powering the expiration date polling.
	 */
	private stopExpiryPolling(): void {
		if (this._timer) {
			clearTimeout(this._timer);
		}
	}

	/**
	 * Get a token for Microsoft Graph API access.
	 * @returns A Microsoft Graph access token as a string.
	 */
	public async getGraphToken(): Promise<string> {
		const request = {
			scopes: ["https://graph.microsoft.com/Mail.Send"],
			account: this._msalContext?.accounts[0]
		};

		try {
			const response = await this.getMsalInstance().acquireTokenSilent(request);
			return response.accessToken;
		} catch (error) {
			// If silent fails, try interactive
			const response = await this.getMsalInstance().acquireTokenPopup(request);
			return response.accessToken;
		}
	}
};

export type InitializedHandler = () => void;